import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { HoverTooltip } from "./HoverTooltip.js";
export const WORKSPACE_TILE_SIZE = 36;
// KTK-274 — narrowed from 60 to match the .ws-rail width in the artboards.
// 36px tile centred in a 56px column = 10px gutter each side, which is the
// minimum that doesn't crop the pending-decision badge at the corner.
export const WORKSPACE_COLUMN_WIDTH = 56;
export function WorkspaceList({ selectedWorkspaceId, onSelect, reloadToken, pendingByWorkspace, }) {
    const ctx = useDataContext();
    const [workspaces, setWorkspaces] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        ctx.hub
            .listWorkspaces()
            .then((ws) => {
            if (cancelled)
                return;
            setWorkspaces(ws);
            if (!selectedWorkspaceId && ws[0])
                onSelect(ws[0]);
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, onSelect, selectedWorkspaceId, reloadToken]);
    if (error) {
        return (_jsxs("div", { style: { color: "#c00", padding: 8, fontSize: 11 }, children: [_jsx("strong", { children: "Hub error:" }), " ", error] }));
    }
    if (!workspaces)
        return null;
    if (workspaces.length === 0) {
        return (_jsx("div", { style: { color: "var(--text-muted)", fontSize: 10, textAlign: "center", padding: 8 }, children: "No workspaces" }));
    }
    return (_jsx("ul", { style: {
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
        }, children: workspaces.map((w) => {
            const selected = w.id === selectedWorkspaceId;
            const pendingDecisions = pendingByWorkspace?.[w.id] ?? 0;
            return (_jsx("li", { style: { width: "100%", display: "flex", justifyContent: "center" }, children: _jsx(HoverTooltip, { content: _jsxs("div", { style: { padding: "2px 10px", whiteSpace: "nowrap", fontWeight: 600 }, children: [w.name, pendingDecisions > 0 && (_jsxs("span", { style: { marginLeft: 8, color: "#f59f00", fontWeight: 700 }, children: [pendingDecisions, " pending"] }))] }), showDelayMs: 30, align: "left", gap: 8, children: _jsx(WorkspaceTile, { workspace: w, selected: selected, onSelect: () => onSelect(w), pendingDecisions: pendingDecisions }) }) }, w.id));
        }) }));
}
function WorkspaceTile({ workspace, selected, onSelect, pendingDecisions, }) {
    const [hovered, setHovered] = useState(false);
    const initials = computeInitials(workspace.name);
    const transform = hovered ? "scale(1.06)" : selected ? "scale(1.02)" : "scale(1)";
    return (_jsxs("span", { style: {
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
        }, children: [_jsx("button", { type: "button", onClick: onSelect, onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), "aria-label": workspace.name, "aria-pressed": selected, style: {
                    width: WORKSPACE_TILE_SIZE,
                    height: WORKSPACE_TILE_SIZE,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    transform,
                    transition: "transform 80ms ease-out, filter 80ms ease-out, opacity 80ms ease-out",
                    filter: hovered ? "brightness(1.15)" : selected ? "none" : "brightness(0.9)",
                    opacity: selected || hovered ? 1 : 0.85,
                    fontFamily: "inherit",
                }, children: _jsx(WorkspaceIcon, { workspace: workspace, initials: initials, animate: hovered }) }), selected && _jsx(ActiveCaret, {}), pendingDecisions > 0 && _jsx(PendingDecisionsBadge, { count: pendingDecisions })] }));
}
function PendingDecisionsBadge({ count }) {
    // KTK-274 — uses the workspace `--decision` token (sienna-red) to match
    // the per-channel decision badge. Sits in the upper-right of the tile
    // and overflows slightly so an ink ring against `--paper-2` reads as a
    // floating chip. Caps at "9+" — counts of 10+ are an oh-no-state and
    // we don't need to render the exact value.
    return (_jsx("span", { "aria-label": `${count} pending ${count === 1 ? "decision" : "decisions"}`, title: `${count} pending ${count === 1 ? "decision" : "decisions"}`, style: {
            position: "absolute",
            top: -3,
            right: -6,
            minWidth: 16,
            height: 16,
            padding: "0 4px",
            borderRadius: 999,
            background: "var(--decision)",
            color: "var(--decision-ink)",
            fontFamily: "var(--mono)",
            fontSize: 9.5,
            fontWeight: 700,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 0 2px var(--paper-2)",
            pointerEvents: "none",
        }, children: count > 9 ? "9+" : count }));
}
function WorkspaceIcon({ workspace, initials, animate, }) {
    const ctx = useDataContext();
    const [resolvedUrl, setResolvedUrl] = useState(null);
    const [renderAs, setRenderAs] = useState("img");
    const videoRef = useRef(null);
    useEffect(() => {
        let cancelled = false;
        setRenderAs("img");
        if (!workspace.icon_url) {
            setResolvedUrl(null);
            return;
        }
        ctx.hub
            .authedImageUrl(workspace.icon_url)
            .then((u) => {
            if (!cancelled)
                setResolvedUrl(u);
        })
            .catch(() => {
            if (!cancelled)
                setResolvedUrl(null);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspace.icon_url]);
    useEffect(() => {
        const v = videoRef.current;
        if (!v)
            return;
        if (animate) {
            void v.play().catch(() => { });
        }
        else {
            v.pause();
            // Snap back to the first frame so the static state is consistent
            // across tiles regardless of where the loop happened to stop.
            try {
                v.currentTime = 0;
            }
            catch {
                // Some codecs throw before metadata loads; the next animate=true
                // will restart from 0 anyway.
            }
        }
    }, [animate, resolvedUrl, renderAs]);
    const tileStyle = {
        width: WORKSPACE_TILE_SIZE,
        height: WORKSPACE_TILE_SIZE,
        borderRadius: 9,
        objectFit: "cover",
        display: "block",
    };
    if (resolvedUrl) {
        if (renderAs === "img") {
            return (_jsx("img", { src: resolvedUrl, alt: workspace.name, style: tileStyle, onError: () => setRenderAs("video") }));
        }
        return (_jsx("video", { ref: videoRef, src: resolvedUrl, loop: true, muted: true, playsInline: true, style: tileStyle }));
    }
    return _jsx(InitialsTile, { initials: initials });
}
function InitialsTile({ initials }) {
    return (_jsx("div", { style: {
            width: WORKSPACE_TILE_SIZE,
            height: WORKSPACE_TILE_SIZE,
            borderRadius: 9,
            backgroundColor: "var(--accent)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0.5,
        }, children: initials }));
}
function ActiveCaret() {
    // KTK-274 — selected-tile indicator: a 2px-wide ink rule on the left edge
    // of the tile, replacing the prior triangle caret. Matches the
    // ws-rail__tile.is-selected pattern from the web-shell artboards.
    return (_jsx("span", { "aria-hidden": true, style: {
            position: "absolute",
            left: -8,
            top: 6,
            bottom: 6,
            width: 2,
            borderRadius: "0 2px 2px 0",
            background: "var(--ink)",
            pointerEvents: "none",
        } }));
}
// First letters of the first two whitespace-separated words; falls back to
// the first two characters of a single-word name. Always uppercase.
export function computeInitials(name) {
    const trimmed = name.trim();
    if (!trimmed)
        return "??";
    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
        return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase();
    }
    return trimmed.slice(0, 2).toUpperCase();
}
//# sourceMappingURL=WorkspaceList.js.map